import { useCallback, useEffect } from 'react'

import { QueryKey } from 'consts/query/queryKey'
import { useIntervalWhileFocused } from 'hooks/useIntervalWhileFocused'
import { useOutdatedQuery } from 'hooks/useOutdatedQuery'
import { useAdditionalFilters } from 'pages/Monitoring/GlobalFilters/AdditionalFilters/AdditionalFiltersContext'
import { TimeRange, TimeScale, VolumeComparisonType } from 'types'

import { checkFutureDateRangeSupported } from '../utils/checkFutureDateRangeSupported'

import { CalculateTimeRangeParamsType } from './useTimeRangeData'

type UseRefetchOnWindowFocusArgs = {
  timeScale: TimeScale
  comparisonType: VolumeComparisonType
  timeRangePages: TimeRange[]
  currentTimeRangeIndex: number
  calculateTimeRangeParams: CalculateTimeRangeParamsType
}

export const useRefetchOnWindowFocus = ({
  timeScale,
  timeRangePages,
  comparisonType,
  currentTimeRangeIndex,
  calculateTimeRangeParams,
}: UseRefetchOnWindowFocusArgs) => {
  const { checkIfQueryIsOutdated } = useOutdatedQuery(QueryKey.GetTicketsVolume)
  const { preparedFiltersForRequest } = useAdditionalFilters()

  const handleResetInitialRange = useCallback(() => {
    const supportsFuture = checkFutureDateRangeSupported({ timeScale, comparisonType })
    const isPast = supportsFuture
      ? currentTimeRangeIndex < timeRangePages.length - 2
      : currentTimeRangeIndex < timeRangePages.length - 1

    if (isPast || !checkIfQueryIsOutdated()) {
      return
    }

    calculateTimeRangeParams({
      timeScale,
      supportsFuture,
      timeframeFilter: preparedFiltersForRequest,
    })
  }, [
    preparedFiltersForRequest,
    currentTimeRangeIndex,
    timeRangePages.length,
    timeScale,
    comparisonType,
    checkIfQueryIsOutdated,
    calculateTimeRangeParams,
  ])

  const { resetInterval } = useIntervalWhileFocused(handleResetInitialRange)

  useEffect(() => {
    resetInterval()
  }, [currentTimeRangeIndex, resetInterval])
}
